import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { StyledSelect } from '@/components/ui/styled-select';
import { Search, Edit, Send, Download, Trash2, CheckCircle, Clock, ListPlus, TrendingUp, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useState } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminUsersTab = ({ users, searchTerm, setSearchTerm, handleUpdateUser }) => {
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', subscription_tier: 'free', role: 'user' });
  const [roleFilter, setRoleFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      name: u.name || '',
      subscription_tier: u.subscription_tier || 'free',
      role: u.role || 'user'
    });
  };
  const saveEdit = async () => {
    await handleUpdateUser(editUser.id, editForm);
    setEditUser(null);
  };

  const filtered = users.filter(u => {
    const matchSearch = !searchTerm ||
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = roleFilter === 'all' || (u.role || 'user') === roleFilter;
    const matchTier = tierFilter === 'all' || (u.subscription_tier || 'free') === tierFilter;
    return matchSearch && matchRole && matchTier;
  });
  const roleBadge = (role) => {
    if (role === 'admin') return 'bg-red-500/20 text-red-400';
    if (role === 'developer') return 'bg-blue-500/20 text-blue-400';
    if (role === 'agent') return 'bg-purple-500/20 text-purple-400';
    return 'bg-white/10 text-text-secondary';
  };
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
          <Input placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white text-black" data-testid="users-search-input" />
        </div>
        <div className="flex gap-3">
          <StyledSelect value={roleFilter} onChange={(v) => setRoleFilter(v)} options={[{value:'all',label:'All Roles'},{value:'user',label:'User'},{value:'admin',label:'Admin'},{value:'developer',label:'Developer'},{value:'agent',label:'Agent'},{value:'broker',label:'Broker'}]} className="min-w-[140px]" data-testid="users-role-filter" />
          <StyledSelect value={tierFilter} onChange={(v) => setTierFilter(v)} options={[{value:'all',label:'All Tiers'},{value:'free',label:'Free'},{value:'premium',label:'Premium'},{value:'elite',label:'Elite'}]} className="min-w-[140px]" data-testid="users-tier-filter" />
        </div>
      </div>
      <div className="glass-card overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-text-secondary font-medium">User</th>
              <th className="text-left p-4 text-text-secondary font-medium">Role</th>
              <th className="text-left p-4 text-text-secondary font-medium">Tier</th>
              <th className="text-left p-4 text-text-secondary font-medium">Joined</th>
              <th className="text-left p-4 text-text-secondary font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-4"><div className="font-semibold">{u.name}</div><div className="text-text-secondary text-sm">{u.email}</div></td>
                <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${roleBadge(u.role)}`}>{u.role || 'user'}</span></td>
                <td className="p-4">
                  <StyledSelect value={u.subscription_tier || 'free'} onChange={(v) => handleUpdateUser(u.id, { subscription_tier: v })} options={[{value:'free',label:'Free'},{value:'premium',label:'Premium'},{value:'elite',label:'Elite'}]} className="h-8 text-sm" data-testid={`user-tier-select-${u.id}`} />
                </td>
                <td className="p-4 text-text-secondary text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <Button variant="ghost" size="sm" className="text-text-secondary hover:text-gold" onClick={() => openEdit(u)} data-testid={`user-edit-btn-${u.id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-text-secondary">No users match the filters</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-obsidian border border-white/10 text-text-primary" data-testid="user-edit-modal">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-text-secondary">Email</Label>
                <p className="text-sm text-text-primary mt-1">{editUser.email}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-text-secondary">Name</Label>
                <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="bg-white text-black mt-1" data-testid="user-edit-name" />
              </div>
              <div>
                <Label className="text-xs uppercase text-text-secondary">Role</Label>
                <StyledSelect value={editForm.role} onChange={(v) => setEditForm({ ...editForm, role: v })} options={[{value:'user',label:'User'},{value:'admin',label:'Admin'},{value:'developer',label:'Developer'},{value:'agent',label:'Agent'},{value:'broker',label:'Broker'},{value:'car_dealer',label:'Car Dealer'},{value:'yacht_dealer',label:'Yacht Dealer'},{value:'interior_designer',label:'Interior Designer'},{value:'other_business',label:'Other Business'}]} className="mt-1" data-testid="user-edit-role" />
              </div>
              <div>
                <Label className="text-xs uppercase text-text-secondary">Subscription Tier</Label>
                <StyledSelect value={editForm.subscription_tier} onChange={(v) => setEditForm({ ...editForm, subscription_tier: v })} options={[{value:'free',label:'Free'},{value:'premium',label:'Premium'},{value:'elite',label:'Elite'}]} className="mt-1" data-testid="user-edit-tier" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="border-white/20">Cancel</Button>
            <Button onClick={saveEdit} className="bg-gold text-black hover:bg-gold-light" data-testid="user-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const AdminWaitlistTab = ({ waitlist, waitlistStats, searchTerm, setSearchTerm, fetchData }) => {
  const filtered = waitlist.filter(w =>
    w.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.phone?.includes(searchTerm)
  );
  const handleSendInvite = async (id) => {
    try { await axios.post(`${API}/admin/waitlist/${id}/send-invite`); toast.success('Invite email sent!'); fetchData(); } catch { toast.error('Failed to send invite'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await axios.delete(`${API}/admin/waitlist/${id}`); toast.success('Entry deleted'); fetchData(); } catch { toast.error('Failed to delete'); }
  };
  const handleExport = () => {
    const csv = [['Name','Email','Phone','Source','Signed Up','Invite Sent'].join(','), ...waitlist.map(w => [w.name,w.email,w.phone,w.source||'landing_page',w.created_at?.split('T')[0]||'',w.invite_sent?'Yes':'No'].join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `dreamlife_waitlist_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success('Exported!');
  };
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6">
          <ListPlus className="w-8 h-8 text-gold mb-4" /><div className="text-3xl font-bold mb-1">{waitlistStats?.total || 0}</div><div className="text-text-secondary text-sm">Total Signups</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card p-6">
          <TrendingUp className="w-8 h-8 text-emerald-400 mb-4" /><div className="text-3xl font-bold mb-1">{waitlistStats?.today || 0}</div><div className="text-text-secondary text-sm">Today's Signups</div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card p-6">
          <Send className="w-8 h-8 text-blue-400 mb-4" /><div className="text-3xl font-bold mb-1">{waitlist.filter(w => w.invite_sent).length}</div><div className="text-text-secondary text-sm">Invites Sent</div>
        </motion.div>
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary w-4 h-4" />
          <Input placeholder="Search waitlist..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white text-black" />
        </div>
        <Button onClick={handleExport} variant="outline" className="border-gold text-gold hover:bg-gold/10"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
      </div>
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              <th className="text-left p-4 text-text-secondary font-medium">Name</th><th className="text-left p-4 text-text-secondary font-medium">Email</th>
              <th className="text-left p-4 text-text-secondary font-medium">Phone</th><th className="text-left p-4 text-text-secondary font-medium">Source</th>
              <th className="text-left p-4 text-text-secondary font-medium">Signed Up</th><th className="text-left p-4 text-text-secondary font-medium">Status</th>
              <th className="text-right p-4 text-text-secondary font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-text-secondary">No waitlist signups yet</td></tr>
              ) : filtered.map((entry, idx) => (
                <tr key={entry.id || `wl-${idx}`} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 font-medium">{entry.name}</td><td className="p-4 text-text-secondary">{entry.email}</td>
                  <td className="p-4 text-text-secondary">{entry.phone}</td>
                  <td className="p-4"><span className="px-2 py-1 bg-white/10 rounded text-xs capitalize">{entry.source || 'landing'}</span></td>
                  <td className="p-4 text-text-secondary text-sm">{entry.created_at?.split('T')[0] || 'N/A'}</td>
                  <td className="p-4">{entry.invite_sent ? <span className="flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle className="w-4 h-4" /> Invited</span> : <span className="flex items-center gap-1 text-yellow-400 text-sm"><Clock className="w-4 h-4" /> Pending</span>}</td>
                  <td className="p-4"><div className="flex justify-end gap-2">
                    {!entry.invite_sent && <Button size="sm" onClick={() => handleSendInvite(entry.id)} className="bg-gold text-black hover:bg-gold-light"><Send className="w-3 h-3 mr-1" /> Send Invite</Button>}
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(entry.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const AdminConciergeTab = ({ conciergeRequests }) => (
  <div className="space-y-4">
    <div className="glass-card p-6">
      <div className="flex items-center gap-3 mb-4"><MessageSquare className="w-6 h-6 text-gold" /><h3 className="text-lg font-semibold">Concierge Requests</h3></div>
      {conciergeRequests.length === 0 ? (
        <p className="text-text-secondary text-center py-8">No concierge requests yet</p>
      ) : (
        <div className="space-y-3">
          {conciergeRequests.map((req, idx) => (
            <div key={req.id || `req-${idx}`} className="p-4 bg-white/5 rounded-lg">
              <div className="flex justify-between mb-2"><span className="font-medium">{req.user_name || 'User'}</span><span className="text-xs text-text-secondary">{new Date(req.created_at).toLocaleDateString()}</span></div>
              <p className="text-text-secondary text-sm">{req.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
